import { useTranslate } from '@tolgee/react';
import { ActionEnum } from 'abi-compliance-common-types';
import { useRouter } from 'next/router';
import * as React from 'react';
import { RouteObject } from '~/app/types';
import { CheersHubIcon, OtherAppsIcon, OurAppsIcon } from '~/shared/components/icons';
import { useMenuRoutes } from '../components/Nav/hooks/useMenuRoutes';
import { useAbility } from './AbilityContext';

type NavContextData = {
  isAppMenuOpen: boolean;
  subMenuOpen?: RouteObject | null;
  menuHeaderItem: RouteObject;
  menuItems: RouteObject[];
  footerItems: RouteObject[];
  isMenuLoading: boolean;
  toggleAppMenu: () => void;
  toggleSubMenu: (item: RouteObject | null) => void;
  checkIfIsActive: (menuRoute: string, subitems?: RouteObject[]) => boolean;
};

const NavContext = React.createContext({} as NavContextData);

type NavProviderProps = {
  children: React.ReactNode;
};

export function NavProvider({ children }: NavProviderProps) {
  const { t } = useTranslate();
  const ability = useAbility();

  const router = useRouter();

  const [isAppMenuOpen, setIsAppMenuOpen] = React.useState(false);
  const [subMenuOpen, setSubmenuOpen] = React.useState<RouteObject | null>();
  const { menuRoutes, footerRoutes, isLoading: isMenuLoading } = useMenuRoutes();

  const toggleSubMenu = (item: RouteObject | null) => {
    setSubmenuOpen(item?.routeComponents?.length ? item : null);
  };

  const checkIfIsActive = React.useCallback(
    (menuRoute: string, subitems?: RouteObject[]) => {
      const hasSomeSubitemActive = subitems?.some(
        subitem => subitem.path === router.route
      );

      return (
        (!!menuRoute && router.route === menuRoute) || !!hasSomeSubitemActive
      );
    },
    [router.route]
  );

  const ourApps: RouteObject[] = React.useMemo(() => {
    const values: RouteObject[] = [];

    if (
      ability.can(ActionEnum.View, 'Cheershub') &&
      process.env.NEXT_PUBLIC_CHEERSHUB_MODULES_DROPDOWN_URL
    ) {
      values.push({
        key: 'cheersHub',
        title: 'CheersHub',
        path: process.env.NEXT_PUBLIC_CHEERSHUB_MODULES_DROPDOWN_URL,
        icon: <CheersHubIcon />,
      });
    }

    if (process.env.NEXT_PUBLIC_WORKDAY_URL) {
      values.push({
        key: 'workday',
        title: 'Workday',
        path: process.env.NEXT_PUBLIC_WORKDAY_URL,
        icon: <OtherAppsIcon />,
      });
    }

    return values;
  }, [ability]);

  function toggleAppMenu() {
    setIsAppMenuOpen(state => !state);
  }

  const providerValue = React.useMemo(
    () => ({
      isAppMenuOpen,
      subMenuOpen,
      menuItems: isMenuLoading ? [] : menuRoutes,
      footerItems: isMenuLoading ? [] : footerRoutes,
      menuHeaderItem: {
        key: 'our_apps',
        title: t('menu.ourApps'),
        path: '/',
        icon: <OurAppsIcon />,
        routeComponents: ourApps,
      },
      isMenuLoading,
      toggleAppMenu,
      toggleSubMenu,
      checkIfIsActive,
    }),
    [isAppMenuOpen, subMenuOpen, menuRoutes, footerRoutes, ourApps, checkIfIsActive, isMenuLoading, t]
  );

  return (
    <NavContext.Provider value={providerValue}>{children}</NavContext.Provider>
  );
}

export function useNav() {
  const context = React.useContext(NavContext);

  if (!context) {
    throw new Error('useNav must be used within a NavProvider');
  }

  return context;
}
